/**
 * userSelectDirective
 *
 * @author Dec Norton <decnorton@gmail.com>
 */

import { attachSelectSearch } from '@shared/directives/inputs';
import isObject from 'lodash-es/isObject';
import isString from 'lodash-es/isString';
import { UserPresenter } from '../user-presenter';

userSelectDirective.$inject = ['$userFetcher', '$errorHandler'];

export function userSelectDirective($userFetcher, $errorHandler) {
    return {
        //language=HTML
        template: `
            <md-input-container>
                <label>{{ label }}</label>

                <md-select name="{{ name }}"
                           md-on-close="_onClose()"
                           ng-model="value"
                           ng-model-options="{ trackBy: '$value.id' }"
                           ng-disabled="disabled"
                           ng-required="required">

                    <md-select-header class="app-form-select-search-container"
                                      ng-if="users.length > 3">
                        <input type="search"
                               class="md-text"
                               ng-model="ui.searchQuery"
                               placeholder="Search for a user...">
                    </md-select-header>

                    <md-option class="app-form-select-option"
                               ng-repeat="user in users | filter:search track by user.id"
                               ng-value="user">

                        <div md-highlight-text="ui.searchQuery"
                             md-highlight-flags="gi">
                            {{ user.label }}
                        </div>

                    </md-option>

                    <md-option ng-if="!users.length"
                               disabled
                               ng-value="null">
                        No users
                    </md-option>
                </md-select>

                <div class="hint" ng-if="hint">{{ hint }}</div>

                <div ng-if="form && name" ng-messages="form[name].$error">
                    <div ng-message="required">Required</div>
                    <div ng-repeat="(key, message) in errorMessages" ng-message="{{ key }}">{{ message }}</div>
                </div>
            </md-input-container>
        `,
        scope: {
            data: '=?data',
            disabled: '=ngDisabled',
            errorMessages: '=errorMessages',
            params: '=?fetcherParams',
            form: '=form',
            hint: '@hint',
            label: '@label',
            name: '@name',
            noFloat: '=?noFloat',
            required: '=?required',
            teams: '=?teams',
            value: '=value',
        },
        link: function(scope, element) {
            element.addClass('app-form-field');
            element.addClass('app-form-select');

            const ui = (scope.ui = {
                searchQuery: '',
            });

            scope.users = [];
            scope.params = angular.isDefined(scope.params) ? scope.params : {};

            scope.search = search;
            scope.clear = clear;

            // Main
            if (angular.isDefined(scope.data)) {
                _setUsers(scope.data);
            } else {
                _fetchUsers();
            }

            scope.$watch('params', params => params && _fetchUsers(), true);
            scope.$watch('teams', teams => teams && _fetchUsers(), true);
            scope.$watch('data', data => _setUsers(data));

            // The md-select directive eats keydown events for some quick select
            // logic. Since we have a search input here, we don't need that logic.
            element.find('input').on('keydown', function(ev) {
                ev.stopPropagation();
            });

            attachSelectSearch(element, scope);

            // Functions

            function clear() {
                scope.value = undefined;

                _onChange();
            }

            function _fetchUsers() {
                const params = {
                    ...(scope.params || {}),
                    fields: 'id',
                    include: ['person:fields(first_name|last_name)'],
                    order_by: 'last_name',
                };

                if (params.hasOwnProperty('team') && !params.team) {
                    params.team = '';
                }

                if (Array.isArray(scope.teams)) {
                    const teams = scope.teams
                        .map(team => {
                            if (isString(team)) {
                                return team;
                            } else if (isObject(team)) {
                                return team.id;
                            }
                        })
                        .filter(Boolean);

                    params.team = teams.join(',');
                }

                return $userFetcher
                    .all(params)
                    .then(response => _setUsers(response.data))
                    .catch(error => {
                        $errorHandler.handle(error);
                    });
            }

            function _onChange() {
                if (angular.isFunction(scope.onChange)) {
                    scope.onChange({
                        value: scope.value,
                    });
                }
            }

            function search(user) {
                if (!ui.searchQuery) {
                    return true;
                }

                const queryParts = ui.searchQuery.toLowerCase().split(' ');

                for (const queryPart of queryParts) {
                    if (user._lowerFirstName.indexOf(queryPart) !== -1) {
                        return true;
                    }

                    if (user._lowerLastName.indexOf(queryPart) !== -1) {
                        return true;
                    }
                }

                return false;
            }

            function _setUsers(users = []) {
                scope.users = UserPresenter.attach(users || []).map(user => {
                    user.label = user.$presenter.name;

                    // Cache lower cache names
                    user._lowerFirstName = user.$presenter.firstName.toLowerCase();
                    user._lowerLastName = user.$presenter.lastName.toLowerCase();
                    user._lowerName = user.$presenter.name.toLowerCase();

                    return user;
                });
            }
        },
    };
}
